import type { ColumnsType } from "antd/lib/table";
import { format, compareAsc } from "date-fns";
import { Tag, Checkbox, InputNumber, Button, Space } from "antd";
import { FilterFilled } from "@ant-design/icons";
import { uploadTypes } from "../../../config";

export const DEFAULT_SOURCE_WINDOW = 30;
export const UNKNOWN_SOURCE = "unknown";

interface DataType {
  pk: string;
  name: string;
  type: string;
  brands: object;
  created: number;
  records: number;
  source?: string | null;
}

// The API can return "snowflake", "ui", null (seen on suppression records),
// or the attribute can be missing entirely. Anything that isn't an explicit
// "snowflake"/"ui" is origin-unknown and must be its own bucket — treating it
// as "ui" (as this used to) silently folded unknown-origin records into the
// UI audit and made every non-Snowflake row compare equal, which broke sort.
const normalizeSource = (source?: string | null) => {
  if (source === "snowflake") return "snowflake";
  if (source === "ui") return "ui";
  return UNKNOWN_SOURCE;
};

interface SourceFilterState {
  sourceWindow: number;
  setSourceWindow: (value: number) => void;
  sourceSortOrder?: "ascend" | "descend";
  uploadedSortOrder?: "ascend" | "descend";
  onResetSort: () => void;
  onApplyFilter: (selectedSourceKeys: string[]) => void;
}

export const columns = (
  app: any,
  {
    sourceWindow,
    setSourceWindow,
    sourceSortOrder,
    uploadedSortOrder,
    onResetSort,
    onApplyFilter,
  }: SourceFilterState
): ColumnsType<DataType> => [
  {
    title: "Segment Name",
    dataIndex: "name",
    align: "left",
  },
  {
    title: "Type",
    dataIndex: "type",
    align: "left",
    render: (type) =>
      uploadTypes.find((item) => item.value === type)?.label || "Emails Only",
  },
  {
    title: "Brands",
    dataIndex: "brands",
    align: "left",
    render: (brands: Array<number>, record: any) =>
      Array.isArray(brands)
        ? record?.type === "scrub"
          ? app.config?.ALL_BRANDS.filter((brand: any) =>
              brands.includes(Number(brand.brandid))
            )
              .map((brand: any) => brand.branddescription)
              .join(", ")
          : app.config?.BRANDS.filter((brand: any) =>
              brands.includes(Number(brand.id))
            )
              .map((brand: any) => brand.name)
              .join(", ")
        : "N/A",
  },
  {
    title: "Records",
    dataIndex: "records",
    align: "right",
    render: (records: number) => records.toLocaleString(),
  },
  {
    title: "Uploaded",
    dataIndex: "created",
    align: "left",
    // Controlled from index.tsx alongside the Source column's sortOrder —
    // once any column controls its own sortOrder, antd stops honoring
    // defaultSortOrder on the others, so both have to be driven the same
    // way for the initial "newest first" state to still apply.
    sortOrder: uploadedSortOrder,
    sorter: (a, b) => compareAsc(new Date(a.created), new Date(b.created)),
    render: (created) =>
      created ? format(new Date(created), "dd MMM yyyy H:mm:ss") : "NaN",
  },
  {
    title: "Source",
    dataIndex: "source",
    align: "left",
    sortOrder: sourceSortOrder,
    sorter: (a, b, sortOrder) => {
      const normA = normalizeSource(a.source);
      const normB = normalizeSource(b.source);
      const aIsUnknown = normA === UNKNOWN_SOURCE;
      const bIsUnknown = normB === UNKNOWN_SOURCE;
      if (aIsUnknown !== bIsUnknown) {
        // Keep N/A pinned to the bottom regardless of direction — antd
        // negates whatever we return here when sortOrder is "descend", so
        // we pre-negate to cancel that out and stop N/A from jumping to
        // the top just because "unknown" happens to sort alphabetically
        // after "snowflake"/"ui".
        const unknownLast = aIsUnknown ? 1 : -1;
        return sortOrder === "descend" ? -unknownLast : unknownLast;
      }
      if (normA !== normB) return normA.localeCompare(normB);
      // Same bucket (both Snowflake, both UI, or both N/A) — break the tie
      // deterministically by upload date (most recent first) instead of
      // leaving it to whatever incidental order the rows happened to be
      // in before this sort was applied.
      const mostRecentFirst = compareAsc(new Date(b.created), new Date(a.created));
      return sortOrder === "descend" ? -mostRecentFirst : mostRecentFirst;
    },
    onFilter: (value, record: any) => normalizeSource(record.source) === value,
    filterIcon: (filtered: boolean) => (
      <FilterFilled
        aria-label="Filter by Source"
        style={{ color: filtered ? "#1677ff" : undefined }}
      />
    ),
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 12, width: 240 }}>
        <div style={{ marginBottom: 4, fontSize: 12, color: "rgba(0,0,0,0.65)" }}>
          Look at the last
        </div>
        <Space style={{ marginBottom: 12 }}>
          <InputNumber
            min={1}
            max={500}
            value={sourceWindow}
            onChange={(value) =>
              setSourceWindow(Number(value) || DEFAULT_SOURCE_WINDOW)
            }
            style={{ width: 80 }}
          />
          <span>records</span>
        </Space>
        <Checkbox.Group
          style={{ display: "flex", flexDirection: "column", gap: 4 }}
          value={selectedKeys as string[]}
          onChange={(checked) => setSelectedKeys(checked as string[])}
          options={[
            { label: "Snowflake", value: "snowflake" },
            { label: "UI", value: "ui" },
            { label: "N/A", value: UNKNOWN_SOURCE },
          ]}
        />
        <Space style={{ marginTop: 12, display: "flex", justifyContent: "space-between" }}>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              setSourceWindow(DEFAULT_SOURCE_WINDOW);
              onResetSort();
              confirm();
            }}
          >
            Reset
          </Button>
          <Button
            size="small"
            type="primary"
            onClick={() => {
              onApplyFilter(selectedKeys as string[]);
              confirm();
            }}
          >
            OK
          </Button>
        </Space>
      </div>
    ),
    render: (source?: string | null) => {
      const normalized = normalizeSource(source);
      if (normalized === "snowflake") return <Tag color="blue">Snowflake</Tag>;
      if (normalized === "ui") return <Tag color="green">UI</Tag>;
      return <Tag color="default">N/A</Tag>;
    },
  },
];
